<template>
  <div
    :class="{
      'service-navigation-item-fi': true,
      'service-navigation-item-fi--brand-background': brandBackground,
    }"
  >
    <button
      v-if="fischer.isProfileFlyoutEnabled() && navigationElement.identifier === 'myAccount'"
      class="service-navigation-item-fi__link"
      :data-test-2="navigationElement.identifier"
      data-test="navigation"
      @click="showProfileFlyout = true"
    >
      <div class="service-navigation-item-fi__link-icon">
        <div class="fi-relative">
          <icon-fi
            class="service-navigation-item-fi__link-icon-user"
            :icon="getIcon(navigationElement)"
            :alt="navigationElement.title"
          />
          <div class="service-navigation-item-fi__bubble">
            <div class="service-navigation-item-fi__bubble-icon-wrapper">
              <icon-fi
                class="service-navigation-item-fi__bubble-icon"
                icon="check"
              />
            </div>
          </div>
        </div>
        <span class="service-navigation-item-fi__icon-text">{{ navigationElement.title }}</span>
      </div>
    </button>
    <a
      v-else
      class="service-navigation-item-fi__link"
      :href="navigationElement.link ? navigationElement.link.url : undefined"
      :aria-label="navigationElement.title"
      :data-test-2="navigationElement.identifier"
      data-test="navigation"
      @click="(evt) => onServiceNavIconClicked(navigationElement, evt)"
    >
      <div class="service-navigation-item-fi__link-icon">
        <div class="fi-relative">
          <icon-fi
            :class="{
              'service-navigation-item-fi__link-icon-user':
                navigationElement.identifier === 'login' ||
                navigationElement.identifier === 'myAccount',
            }"
            :icon="getIcon(navigationElement)"
            :alt="navigationElement.title"
          />
          <header-cart-fi
            v-if="['retailConnectCart', 'shopCart'].includes(navigationElement.identifier)"
            class="service-navigation-item-fi__bubble"
            data-test="cart-count"
          />
          <div
            v-if="navigationElement.identifier === 'myAccount'"
            class="service-navigation-item-fi__bubble"
          >
            <div class="service-navigation-item-fi__bubble-icon-wrapper">
              <icon-fi
                class="service-navigation-item-fi__bubble-icon"
                icon="check"
              />
            </div>
          </div>
        </div>
        <span
          v-if="
            navigationElement.identifier === 'login' || navigationElement.identifier === 'myAccount'
          "
          class="service-navigation-item-fi__icon-text"
        >
          {{ navigationElement.title }}
        </span>
      </div>
    </a>
  </div>
</template>

<script setup lang="ts">
import { IconFi } from 'atoms';
import { useNavigationStore } from 'store/navigation';
import { useProfileFlyoutStore } from 'store/profile-flyout';
import { computed, toRefs } from 'vue';

import { useCountrySelect } from '@/composables/countrySelect';
import { useFischer } from '@/composables/fischerPlugin';
import type { ServiceNavigationElement } from '@/sitecoreState';

import HeaderCartFi from '../header-cart-fi/HeaderCartFi.vue';
import iconMappings, { type IconMappingKeys } from './iconMappings';

defineOptions({
  name: 'ServiceNavigationItemFi',
});

const { isSingleLocale, getLocaleData, countrySelectUrl } = useCountrySelect();
const { showProfileFlyout } = toRefs(useProfileFlyoutStore());

const navigationStore = useNavigationStore();
const fischer = useFischer();
const props = defineProps<{
  navigationElement: ServiceNavigationElement;
  brandBackground?: boolean;
}>();

const emit = defineEmits<{
  'toggle-flyout-icon': [value: void];
  login: [value: void];
}>();

const isCountrySelectActive = computed<boolean>(() => navigationStore.isCountrySelect);

const isSelectCountry = computed<boolean>(
  () => props.navigationElement.identifier === 'selectCountry',
);

const getIcon = (navigationElement: ServiceNavigationElement) => {
  const key: IconMappingKeys = navigationElement.identifier;
  return iconMappings[key];
};

const onServiceNavIconClicked = async (
  navigationElement: ServiceNavigationElement,
  evt: MouseEvent,
) => {
  if (isSelectCountry.value) {
    evt.preventDefault();
    evt.stopImmediatePropagation();
    await getLocaleData();

    if (isSingleLocale.value) {
      const url = navigationElement?.link?.url;
      const targetUrl = countrySelectUrl(url);
      if (targetUrl) {
        window.location.href = targetUrl;
      }

      return;
    }

    navigationStore.setShowFlyout(!isCountrySelectActive.value);
    navigationStore.setIsCountrySelect(!isCountrySelectActive.value);
  }

  if (navigationElement.identifier === 'login') {
    if (!fischer.isLoginDialogEnabled()) {
      return;
    }
    if (evt) {
      evt.preventDefault?.();
      if (evt.detail === 1) {
        emit('login');
      }
    }
    return;
  }

  emit('toggle-flyout-icon');
};
</script>

<style scoped src="./service-navigation-item-fi" />
